import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { AuthContext, type AuthContextValue } from './auth-context';
import type { User } from '@/types';
import {
  signInWithGoogle,
  signOut as nativeSignOut,
  getIdToken,
  onAuthChange,
  refreshAuth,
  getPlatformInfo,
  type User as FirebaseUser
} from '@/lib/native-auth';

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Convert Firebase user to our User type
 */
function firebaseUserToUser(firebaseUser: FirebaseUser): User {
  return {
    id: firebaseUser.uid,
    email: firebaseUser.email || '',
    name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
    picture: firebaseUser.photoURL || undefined,
  };
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const platformInfo = getPlatformInfo();

  /**
   * Create a session with the BFF using Firebase ID token
   */
  const createSession = useCallback(async (firebaseUser: FirebaseUser) => {
    try {
      const idToken = await firebaseUser.getIdToken();

      const response = await fetch('/auth/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ idToken }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to create session');
      }

      // Session created successfully, set user
      setUser(firebaseUserToUser(firebaseUser));
      setError(null);
    } catch (err) {
      console.error('Session creation failed:', err);
      setError(err instanceof Error ? err.message : 'Session creation failed');
      // Sign out if session creation fails
      await nativeSignOut();
      setUser(null);
    }
  }, []);

  /**
   * Listen to auth state changes
   */
  useEffect(() => {
    const unsubscribe = onAuthChange(async (firebaseUser) => {
      if (firebaseUser) {
        // User signed in, create BFF session
        await createSession(firebaseUser);
      } else {
        // User signed out
        setUser(null);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [createSession]);

  /**
   * Refresh auth on app resume (mobile)
   */
  useEffect(() => {
    if (!platformInfo.isNative) return;

    // Refresh auth when app becomes active
    const handleAppStateChange = () => {
      if (document.visibilityState === 'visible' && user) {
        refreshAuth().catch(console.error);
      }
    };

    document.addEventListener('visibilitychange', handleAppStateChange);

    return () => {
      document.removeEventListener('visibilitychange', handleAppStateChange);
    };
  }, [platformInfo.isNative, user]);

  /**
   * Sign in with Google
   * Uses native auth on iOS/Android, popup on web
   */
  const signIn = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      console.log(`Signing in on ${platformInfo.platform}...`);
      // Native auth on iOS/Android, popup on web
      // The onAuthChange listener will handle session creation
      await signInWithGoogle();
    } catch (err) {
      console.error('Sign in failed:', err);
      setError(err instanceof Error ? err.message : 'Sign in failed');
      setIsLoading(false);
    }
  }, [platformInfo.platform]);

  /**
   * Sign out
   * Handles native sign-out on iOS/Android
   */
  const signOut = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Clear BFF session
      await fetch('/auth/logout', {
        method: 'POST',
        credentials: 'include',
      }).catch(() => {
        // Ignore errors - BFF may not be available
      });

      // Sign out (handles native + Firebase)
      await nativeSignOut();
      setUser(null);
    } catch (err) {
      console.error('Sign out failed:', err);
      setError(err instanceof Error ? err.message : 'Sign out failed');
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Get access token - returns Firebase ID token for API calls
   * Note: With BFF pattern, this is mainly for direct Firebase calls
   * API calls go through BFF which uses session cookies
   */
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    return getIdToken();
  }, []);

  const value: AuthContextValue = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    signIn,
    signOut,
    getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
